import { ofType, combineEpics } from 'redux-observable';
import { merge, zip, of, from } from 'rxjs';
import {
    debounceTime,
    takeUntil,
    tap,
    map,
    mergeMap,
    filter,
    distinctUntilChanged,
    share,
    switchMap,
} from 'rxjs/operators';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import cond from 'lodash/cond';
import { camelizeKeys } from 'humps';
import { getInstance as getBootstrap } from 'utils/bootstrap';

import wkPostMessage from 'standalone/splash-page/mobile-rsvp/wkwebview-postmessage';

import { getFormType, getErrorMessages, getFormVersion, getCached, getAllForms } from 'common/entities/form/selectors';

import { FORM_TYPE_TICKET_SELECTION, FORM_TYPE_RSVP } from 'common/entities/form';
import {
    getAllFormNodeEntities,
    getAllFormNodes,
    getFormNodes,
    getCaptchaQuestions,
} from 'common/entities/formNode/selectors';
import { getLogicEntities, getAllLogicRules, getFormLogicRules } from 'common/entities/formLogicRule/selectors';
import {
    DEFAULT_SERVER_MESSAGES,
    ERROR_QUESTION_RESPONSE_LIMIT,
    SERVER_ERROR_MESSAGE,
    ERROR_CAPTCHA_DATADOME,
    ERROR_SERVER_DEFAULT,
} from 'common/entities/form/errorMessages';
import { LOADED_FORM_DATA } from 'common/compounds/FormBuilder/constants/actionTypes';
import { getTicketTypes } from 'common/entities/ticketType/selectors';
import { getUrlParams } from 'common/utils/getUrlParams';
import { loadResponseAvailability, mapUrlData, setSessionsStep } from '../actions';
import {
    submitNewRsvp as newRsvpSubmissionRequest,
    submitUpdatedRsvp as updatingSubmissionRequest,
} from '../utils/submitFormData';
import validateSubmission from '../utils/onSubmitValidate';
import getNodesVisibility from '../utils/getNodesVisibility';
import createSubmissionPayload from '../utils/cakeSubmissionUtils';
import getMobileRsvpParams from '../utils/mobileSubmissionUtils';
import {
    submitTickets as submitTicketsSyncronous,
    validateTopLevelErrors,
    validateFields,
    validateEmailPerOrderErrors,
    validateOneOrderPerEmail,
} from '../utils/ticketSubmission';

import {
    getEventId,
    getGuestAccessHash,
    getIsUpdating,
    getSubmitUrl,
    getIsMobileRsvp,
    getLimitOneOrderPerEmail,
} from '../selectors';
import {
    getFormValues,
    getFormGroupValues,
    getAllFormValues,
    getFormSubmitting,
    getFormErrors,
    getSelectedSessionIds,
} from '../selectors/form';

import {
    updateFormValue,
    setFormVisibility,
    setFormGroupVisibility,
    setFormErrors,
    setFormValues,
    setFormTouched,
    setFormSubmitting,
    setFormMetas,
    updateFormDefaultValue,
} from '../actions/form';

import { UPDATE_FORM_GROUP_VALUE, UPDATE_FORM_VALUE, SET_FORM_VALUES } from '../constants/actionTypes';

import {
    UPDATE_FORM_NODE,
    UPDATE_FORM_NODE_CANCEL_PREVIOUS,
    RESET_FORM,
    VALIDATE_SUBMISSION,
    VALIDATE_TICKET_SUBMISSION,
    VALIDATE_ERRORS_AND_START_SUBMISSION,
    START_SUBMISSION,
    SUBMIT_SUBMISSION,
    MOBILE_RSVP_SUBMISSION,
    FETCH_SUBMISSION,
    SUCCESS_REQUEST_SUBMISSION,
    FAILED_REQUEST_SUBMISSION,
    GA_FORM_SUCCESSFULLY_FINISHED_EFFECT,
    GA_FORM_START_EFFECT,
    GA_FORM_ERROR_EFFECT,
    SET_URL_PARAM_EFFECT,
    VALIDATE_GUEST_INFORMATION,
    SESSION_REGISTRATION_FORM_SUBMISSION,
} from '../constants/epicTypes';

import { TICKET_EMAIL } from '../constants/ticketTypes';
import { FORM_SESSIONS_STEPS } from '../constants/sessions';

// this prevents importing effects from a different standalone entry
const LOADED_FORM_DATA_EFFECT = '@effect/checkout/LOADED_FORM_DATA';

// Effect Creators

export const createEffect = (type) => (meta) => ({
    type,
    meta,
});

export const updateFormNodeEffect = createEffect(UPDATE_FORM_NODE);
export const updateFormNodeCancelPreiousEffect = createEffect(UPDATE_FORM_NODE_CANCEL_PREVIOUS);
export const resetFormEffect = createEffect(RESET_FORM);

export const startSubmissionEffect = (msg) => ({
    type: START_SUBMISSION,
    meta: msg,
});

const validateSubmissionEffect = (msg) => ({
    type: VALIDATE_SUBMISSION,
    meta: msg,
});

const submitSubmissionEffect = (msg) => ({
    type: SUBMIT_SUBMISSION,
    meta: msg,
});

const mobileRSvpSubmissionEffect = () => ({
    type: MOBILE_RSVP_SUBMISSION,
});

const fetchSubmissionEffect = (msg) => ({
    type: FETCH_SUBMISSION,
    meta: msg,
});

const successRequestSubmissionEffect = (msg) => ({
    type: SUCCESS_REQUEST_SUBMISSION,
    meta: msg,
});

export const failedRequestSubmissionEffect = (msg) => ({
    type: FAILED_REQUEST_SUBMISSION,
    meta: msg,
});

const reportGAFormSuccessfullyFinished = () => ({
    type: GA_FORM_SUCCESSFULLY_FINISHED_EFFECT,
    meta: undefined,
});

export const reportGAFormStarted = () => ({
    type: GA_FORM_START_EFFECT,
    meta: undefined,
});

export const reportGAFormError = () => ({
    type: GA_FORM_ERROR_EFFECT,
    meta: undefined,
});

export const validateGuestInformationEffect = (msg) => ({
    type: VALIDATE_GUEST_INFORMATION,
    meta: msg,
});

export const submitSessionRegistrationFormEffect = (msg) => ({
    type: SESSION_REGISTRATION_FORM_SUBMISSION,
    meta: msg,
});

// Epics

const bootstrap = getBootstrap();
const bootstrapFlags = bootstrap.get('flags') || {};

const updateFormNodeCancelPreviousEpic = (action$, state$) =>
    action$.pipe(
        ofType(UPDATE_FORM_NODE_CANCEL_PREVIOUS),
        map(({ meta: { id, value, group } }) => updateFormValue(id, value, group)),
    );

const updateFormNodeEpic = (action$, state$) =>
    action$.pipe(
        ofType(UPDATE_FORM_NODE),
        (bootstrapFlags.formRendererPerformanceImprovements ? debounceTime : tap)(500),
        switchMap(({ meta: { id, value, group } }) =>
            of(updateFormValue(id, value, group), reportGAFormStarted()).pipe(
                takeUntil(action$.pipe(ofType(UPDATE_FORM_NODE_CANCEL_PREVIOUS))),
            ),
        ),
    );

const visibilityEpic = (action$, state$) => {
    const formNodesChange$ = state$.pipe(
        filter((state) => state && Object.keys(getAllFormNodeEntities(state)).length !== 0),
        map((state) => getAllFormNodeEntities(state)),
        distinctUntilChanged(),
    );

    const formLogicRulesChange$ = state$.pipe(
        filter((state) => state && Object.keys(getLogicEntities(state)).length !== 0),
        map((state) => getLogicEntities(state)),
        distinctUntilChanged(),
    );

    const formEntitiesChange$ = zip(formNodesChange$, formLogicRulesChange$);

    // TODO: Refactor to recalculate form values visibility granularly
    const formValueChange$ = action$.pipe(
        ofType(UPDATE_FORM_VALUE, SET_FORM_VALUES, LOADED_FORM_DATA, LOADED_FORM_DATA_EFFECT),
    );

    return merge(formEntitiesChange$, formValueChange$).pipe(
        mergeMap(({ meta }) => {
            const state = state$.value;
            const formNodes = getAllFormNodes(state);
            const formLogicRules = getAllLogicRules(state);
            const formValues = getFormValues(state);

            const visibility = getNodesVisibility(formNodes, formLogicRules, formValues);
            const groupId = meta;

            return of(setFormVisibility(visibility, groupId), createEffect(SET_URL_PARAM_EFFECT)({ group: groupId }));
        }),
    );
};

const urlParamEpic = (action$, state$) =>
    action$.pipe(
        ofType(SET_URL_PARAM_EFFECT),
        filter(({ meta }) => {
            const state = state$.value;

            const formNodes = getAllFormNodes(state);
            const urlData = getUrlParams();

            const urlValues = urlData ? mapUrlData(formNodes, urlData) : {};

            const group = get(meta, 'group');

            return group && !isEmpty(urlValues);
        }),
        map(({ meta }) => {
            const state = state$.value;

            const formNodes = getAllFormNodes(state);
            const urlData = getUrlParams();

            const urlValues = mapUrlData(formNodes, urlData);

            const group = get(meta, 'group');
            const formValues = getFormGroupValues(state, { group });

            const values = {
                ...formValues,
                ...urlValues,
            };

            return setFormValues(values, group);
        }),
    );

const visibilityUpdateFormGroupEpic = (action$, state$) =>
    action$.pipe(
        ofType(UPDATE_FORM_GROUP_VALUE),
        map(({ payload: { group } }) => {
            const state = state$.value;
            const formNodes = Object.values(getAllFormNodes(state));
            const formLogicRules = Object.values(getAllLogicRules(state));
            const formValues = getFormGroupValues(state, { group });

            const visibility = getNodesVisibility(formNodes, formLogicRules, formValues);

            return setFormGroupVisibility(visibility, group);
        }),
    );

const ticketSubmissionValidationEpic = (action$, state$) =>
    action$.pipe(
        ofType(START_SUBMISSION),
        filter(() => !getFormSubmitting(state$.value)),
        filter(({ meta: { formId } }) => getFormType(state$.value, formId) === FORM_TYPE_TICKET_SELECTION),
        filter(() => !getLimitOneOrderPerEmail(state$.value)),
        map(({ meta: { allowSubmit } }) => ({ allowSubmit })),
        map(createEffect(VALIDATE_ERRORS_AND_START_SUBMISSION)),
    );

const getObservableFromRequestLimitOrderPerEmail = (request) => from(request);

const ticketSubmissionValidationEpicWithLimitOrderPerEmailEpic = (
    action$,
    state$,
    { requestObservable = getObservableFromRequestLimitOrderPerEmail } = {},
) =>
    action$.pipe(
        ofType(START_SUBMISSION),
        filter(() => !getFormSubmitting(state$.value)),
        filter(({ meta: { formId } }) => getFormType(state$.value, formId) === FORM_TYPE_TICKET_SELECTION),
        filter(() => getLimitOneOrderPerEmail(state$.value)),
        mergeMap(({ meta: { allowSubmit } }) =>
            merge(
                of(setFormSubmitting(true)),
                requestObservable(validateOneOrderPerEmail(getAllFormValues(state$.value)[TICKET_EMAIL])).pipe(
                    map((response) => {
                        const { status } = response.data;

                        return {
                            allowSubmit,
                            status,
                        };
                    }),
                    map(createEffect(VALIDATE_ERRORS_AND_START_SUBMISSION)),
                ),
            ),
        ),
    );

const checkErrorsAndSubmitEpic = (action$, state$) =>
    action$.pipe(
        ofType(VALIDATE_ERRORS_AND_START_SUBMISSION),
        map(({ meta: { allowSubmit, status } }) => {
            const limitOneOrderPerEmail = getLimitOneOrderPerEmail(state$.value);
            const formValues = getAllFormValues(state$.value);
            const requiredFields = [limitOneOrderPerEmail && TICKET_EMAIL].filter(Boolean);

            const emailPerOrderErrors = validateEmailPerOrderErrors(status);

            const ticketTypes = getTicketTypes(state$.value);

            const topLevelErrors = validateTopLevelErrors(formValues, ticketTypes);
            const fieldErrors = validateFields(formValues, requiredFields);

            const errors = {
                ...emailPerOrderErrors,
                ...topLevelErrors,
                ...fieldErrors,
            };

            return {
                valid: Object.keys(errors).length === 0 && allowSubmit,
                errors,
            };
        }),
        map(createEffect(VALIDATE_TICKET_SUBMISSION)),
    );

const validTicketSubmissionEpic = (action$, state$, { submitTickets = submitTicketsSyncronous } = {}) =>
    action$.pipe(
        ofType(VALIDATE_TICKET_SUBMISSION),
        filter(({ meta: { valid } }) => valid),
        tap(() => {
            const formId = getAllForms(state$.value).map((form) => form.id)[0];
            const formValues = getAllFormValues(state$.value);
            const formVersion = getFormVersion(state$.value, formId);
            const formCached = getCached(state$.value, formId);
            submitTickets(formValues, formVersion, formCached);
        }),
        map(() => setFormSubmitting(true)),
    );

const notValidTicketSubmissionEpic = (action$, state) =>
    action$.pipe(
        ofType(VALIDATE_TICKET_SUBMISSION),
        filter(({ meta: { valid } }) => !valid),
        map(({ meta: { errors } }) => errors),
        mergeMap((errors) => of(setFormErrors(errors), setFormSubmitting(false))),
    );

const rsvpSubmissionValidationEpic = (action$, state$) =>
    action$.pipe(
        ofType(START_SUBMISSION),
        filter(() => !getFormSubmitting(state$.value)),
        filter(({ meta: { formId } }) => getFormType(state$.value, formId) === FORM_TYPE_RSVP),
        mergeMap(({ meta: { allowSubmit, formId } }) => {
            const state = state$.value;
            const formNodes = getFormNodes(state, formId);
            const formLogicRules = getFormLogicRules(state, formId);
            const formValues = getFormValues(state);
            const isUpdating = getIsUpdating(state);

            const [errors, metaErrors] = validateSubmission(formNodes, formLogicRules, formValues, isUpdating);
            const haveErrors = Object.keys(errors).length;
            const haveMetaErrors = Object.values(metaErrors).filter((value) => !!Object.values(value)[0]).length;
            const canSubmit = !!(!haveErrors && !haveMetaErrors && allowSubmit);
            const prevErrors = getFormErrors(state$.value, formId);

            return from(
                [
                    setFormErrors(errors),
                    metaErrors && Object.keys(metaErrors).length > 0 && setFormMetas(metaErrors),
                    !canSubmit && reportGAFormError(),
                    validateSubmissionEffect({
                        errors,
                        canSubmit,
                        formId,
                        prevErrors,
                    }),
                ].filter(Boolean),
            );
        }),
    );

const validRsvpSubmissionEpic = (action$, state$) =>
    action$.pipe(
        ofType(VALIDATE_SUBMISSION),
        filter(({ meta: { canSubmit } }) => canSubmit),
        map(({ meta: { formId, prevErrors } }) => {
            const state = state$.value;
            const formNodes = getFormNodes(state, formId);
            const formLogicRules = getFormLogicRules(state, formId);
            const formValues = getFormValues(state);
            const eventId = getEventId(state);
            const isMobileRsvp = getIsMobileRsvp(state);
            const isUpdating = getIsUpdating(state);
            const formSubmitUrl = getSubmitUrl(state);
            const formVersion = getFormVersion(state, formId);
            const cached = getCached(state, formId);
            const sessions = getSelectedSessionIds(state, formId);

            const params = {
                ...createSubmissionPayload(formNodes, formLogicRules, formValues, sessions),
                formVersion,
                cached,
            };

            return submitSubmissionEffect({
                eventId,
                isMobileRsvp,
                isUpdating,
                formSubmitUrl,
                params,
                formId,
                prevErrors,
            });
        }),
    );

const mobileRsvpSubmissionEpic = (action$, state$, { submitMobileRsvp = wkPostMessage } = {}) =>
    action$.pipe(
        ofType(SUBMIT_SUBMISSION),
        filter(({ meta: { isMobileRsvp } }) => isMobileRsvp),
        tap(({ meta: { params, eventId } }) => {
            const rsvpParams = getMobileRsvpParams(params, eventId);
            submitMobileRsvp('submit', rsvpParams);
        }),
        map(mobileRSvpSubmissionEffect),
    );

// This piece of code here is isolating the translation of the request to an observable
// Doing that allows us to test epics that relay on promise based requests, using dependency injection
// like in 'desktopRsvpSubmissionEpic'
// More context about troubles with promises vs marble testing here:
// https://github.com/ReactiveX/rxjs/blob/master/doc/marble-testing.md#you-cant-directly-test-rxjs-code-that-consumes-promises-or-uses-any-of-the-other-schedulers-eg-asapscheduler
const getObservableFromRequest = (request, params, formId, appState, prevErrors) =>
    from(
        request
            .then((response) => ({ response, params, formId }))
            .catch((error) => ({
                response: {
                    status: 'error',
                    msgBody: SERVER_ERROR_MESSAGE,
                    errorCode: cond([
                        [
                            () => error?.response?.data?.url && error?.response?.status === 403,
                            () => ERROR_CAPTCHA_DATADOME,
                        ],
                        [() => true, () => ERROR_SERVER_DEFAULT],
                    ])(),
                },
                params,
                formId,
            })),
    );

const desktopRsvpSubmissionEpic = (
    action$,
    state$,
    {
        requestObservable = getObservableFromRequest,
        submitNewRsvp = newRsvpSubmissionRequest,
        submitUpdatedRsvp = updatingSubmissionRequest,
    } = {},
) => {
    const desktopSubmission$ = action$.pipe(
        ofType(SUBMIT_SUBMISSION),
        filter(({ meta: { isMobileRsvp } }) => !isMobileRsvp),
    );

    const initSubmission$ = desktopSubmission$.pipe(
        mergeMap(() => of(setFormSubmitting(true), fetchSubmissionEffect())),
    );

    const defaultSubmission$ = desktopSubmission$.pipe(
        filter(({ meta: { isUpdating } }) => !isUpdating),
        mergeMap(({ meta: { params, formSubmitUrl, formId, prevErrors } }) =>
            requestObservable(submitNewRsvp(formSubmitUrl, params), params, formId, state$.value, prevErrors),
        ),
    );

    const updatingSubmission$ = desktopSubmission$.pipe(
        filter(({ meta: { isUpdating } }) => isUpdating),
        mergeMap(({ meta: { params, formSubmitUrl, formId, prevErrors } }) => {
            const guestAccessHash = getGuestAccessHash(state$.value);

            return requestObservable(
                submitUpdatedRsvp(formSubmitUrl, params, guestAccessHash),
                params,
                formId,
                state$.value,
                prevErrors,
            );
        }),
    );

    const request$ = merge(defaultSubmission$, updatingSubmission$).pipe(share());

    const successRequest$ = request$.pipe(
        filter(({ response }) => response.status !== 'error'),
        mergeMap((requestData) =>
            of(
                setFormErrors({}),
                reportGAFormSuccessfullyFinished(getEventId(state$.value)),
                setFormValues({}),
                setFormTouched({}),
                setFormSubmitting(false),
                successRequestSubmissionEffect(requestData),
            ),
        ),
    );

    const failedRequest$ = request$.pipe(
        filter(({ response }) => response.status === 'error'),
        map(failedRequestSubmissionEffect),
    );

    return merge(initSubmission$, successRequest$, failedRequest$);
};

const failedRequestRsvpSubmissionEpic = (action$, state$) =>
    action$.pipe(
        ofType(FAILED_REQUEST_SUBMISSION),
        mergeMap(({ meta: { response: rawResponse, formId } }) => {
            if (formId === undefined) {
                throw new Error('Need to pass formId');
            }

            const state = state$.value;
            const response = camelizeKeys(rawResponse);

            const capchaQuestionUpdateActions = getCaptchaQuestions(state, formId).map((node) =>
                updateFormValue(node.id, null, false),
            );

            const formErrorMessages = getErrorMessages(state, formId);
            const errorCode = get(response, 'errorCode');
            const errorMessage = formErrorMessages[errorCode] || DEFAULT_SERVER_MESSAGES[errorCode] || response.msgBody;

            const formattedFormNodeErrors = get(response, 'questionErrors', [])
                .filter((error) => get(error, 'formNode.id'))
                .reduce(
                    (memo, { formNode, type, message }) => ({
                        ...memo,
                        [formNode.id]: [formErrorMessages[type] || DEFAULT_SERVER_MESSAGES[type] || message],
                    }),
                    {},
                );

            const responseLimitQuestionErrors = get(response, 'questionErrors', []).filter(
                (error) => error.type === ERROR_QUESTION_RESPONSE_LIMIT && get(error, 'formNode.id'),
            );

            const errors = {
                0: [errorMessage],
                ...formattedFormNodeErrors,
            };
            return from(
                [
                    ...capchaQuestionUpdateActions,
                    setFormErrors(errors),
                    reportGAFormError(),
                    setFormSubmitting(false),
                    setSessionsStep(FORM_SESSIONS_STEPS.GUEST_INFORMATION),
                    // if limit has been reached for a selected option, we'd like to reload availability
                    // and clear the answer.
                    ...(!responseLimitQuestionErrors.length
                        ? []
                        : [
                              ...responseLimitQuestionErrors.map((error) =>
                                  updateFormDefaultValue(error.formNode.id, null),
                              ),
                              loadResponseAvailability(),
                          ]),
                ].filter(Boolean),
            );
        }),
    );

const resetFormEpic = (action$) =>
    action$.pipe(
        ofType(RESET_FORM),
        mergeMap(() => of(setFormErrors({}), setFormValues({}), setFormSubmitting(false))),
    );

const rsvpGuestInformationValidationEpic = (action$, state$) =>
    action$.pipe(
        ofType(VALIDATE_GUEST_INFORMATION),
        filter(({ meta: { formId } }) => getFormType(state$.value, formId) === FORM_TYPE_RSVP),
        mergeMap(({ meta: { formId, isPreview } }) => {
            const state = state$.value;
            const formNodes = getFormNodes(state, formId);
            const formLogicRules = getFormLogicRules(state, formId);
            const formValues = getFormValues(state);
            const isUpdating = getIsUpdating(state);

            const [errors, metaErrors] = validateSubmission(formNodes, formLogicRules, formValues, isUpdating);
            const haveErrors = Object.keys(errors).length;
            const haveMetaErrors = Object.values(metaErrors).filter((value) => !!Object.values(value)[0]).length;
            const canGoToNextStep = !!(!haveErrors && !haveMetaErrors && !isPreview);

            return from(
                [
                    setFormErrors(errors),
                    metaErrors && Object.keys(metaErrors).length > 0 && setFormMetas(metaErrors),
                    canGoToNextStep && setSessionsStep(FORM_SESSIONS_STEPS.SESSION_REGISTRATION),
                ].filter(Boolean),
            );
        }),
    );

const rsvpSessionRegistrationFormSubmissionEpic = (action$, state$) =>
    action$.pipe(
        ofType(SESSION_REGISTRATION_FORM_SUBMISSION),
        map(({ meta: { formId, isPreview } }) => {
            const errors = getFormErrors(state$.value);
            return validateSubmissionEffect({ errors, canSubmit: !isPreview, formId, prevErrors: {} });
        }),
    );

export const visibilityEpics = combineEpics(visibilityEpic);

export const submissionEpics = combineEpics(
    ticketSubmissionValidationEpic,
    checkErrorsAndSubmitEpic,
    ticketSubmissionValidationEpicWithLimitOrderPerEmailEpic,
    validTicketSubmissionEpic,
    notValidTicketSubmissionEpic,
    rsvpSubmissionValidationEpic,
    validRsvpSubmissionEpic,
    mobileRsvpSubmissionEpic,
    desktopRsvpSubmissionEpic,
    failedRequestRsvpSubmissionEpic,
    rsvpGuestInformationValidationEpic,
    rsvpSessionRegistrationFormSubmissionEpic,
);

export default combineEpics(
    updateFormNodeCancelPreviousEpic,
    updateFormNodeEpic,
    resetFormEpic,
    visibilityEpics,
    visibilityUpdateFormGroupEpic,
    submissionEpics,
    checkErrorsAndSubmitEpic,
    ticketSubmissionValidationEpicWithLimitOrderPerEmailEpic,
    urlParamEpic,
);
